import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Send, Clock, Globe, UserRound } from 'lucide-react'
import SEO from '../components/SEO'

// Google reCAPTCHA configuration
const RECAPTCHA_SITE_KEY = '6Le3TxATAAAAADii9TGt3etFVVtgiz5tVzZ6JMCa'

// Load reCAPTCHA script
const loadReCaptchaScript = () => {
  return new Promise((resolve, reject) => {
    // Check if reCAPTCHA script is already loaded
    if (window.grecaptcha) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
    if (existingScript) {
      existingScript.onload = () => resolve();
      existingScript.onerror = () => reject(new Error('reCAPTCHA script failed to load'));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log("reCAPTCHA script loaded successfully.");
      resolve();
    };
    script.onerror = (error) => {
      console.error("Failed to load reCAPTCHA script. Check your site key:", RECAPTCHA_SITE_KEY);
      reject(new Error(`reCAPTCHA script failed to load. Site key: ${RECAPTCHA_SITE_KEY}`));
    };
    document.head.appendChild(script);
  });
}

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    'g-recaptcha-response': ''
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)
  const [recaptchaError, setRecaptchaError] = useState(null)
  const recaptchaRef = useRef(null)
  const recaptchaWidgetId = useRef(null)

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;

    const cleanupRecaptcha = () => {
      if (window.grecaptcha && recaptchaWidgetId.current !== null) {
        try {
          window.grecaptcha.reset(recaptchaWidgetId.current);
          console.log('Reset widget during cleanup:', recaptchaWidgetId.current);
        } catch (error) {
          console.log('Error resetting reCAPTCHA during cleanup:', error);
        }
      }
      
      if (recaptchaRef.current) {
        // Remove all child nodes
        while (recaptchaRef.current.firstChild) {
          recaptchaRef.current.removeChild(recaptchaRef.current.firstChild);
        }
        // Also clear innerHTML as backup
        recaptchaRef.current.innerHTML = '';
      }
      
      recaptchaWidgetId.current = null;
    };

    const initializeRecaptcha = async () => {
      try {
        setRecaptchaError(null);
        setRecaptchaLoaded(false);
        
        // Clean up any existing reCAPTCHA
        cleanupRecaptcha();
        
        await loadReCaptchaScript();
        
        if (!mounted) return;

        // Wait a bit for grecaptcha to fully initialize
        const maxAttempts = 10;
        const timeoutMs = 30000; // 30 seconds total timeout
        let attempts = 0;
        const startTime = Date.now();

        const tryRender = () => {
          if (!mounted) return;
          
          // Check for timeout
          if (Date.now() - startTime > timeoutMs) {
            if (mounted) {
              setRecaptchaError('reCAPTCHA failed to load within 30 seconds. Please refresh the page and try again.');
              setRecaptchaLoaded(false);
            }
            return;
          }

          if (window.grecaptcha && window.grecaptcha.render && recaptchaRef.current) {
            try {
              // Double-check that the container is empty and ready
              if (recaptchaRef.current.children.length > 0) {
                recaptchaRef.current.innerHTML = '';
              }

              // Check if grecaptcha is ready and the container is available
              if (window.grecaptcha.getResponse !== undefined && recaptchaRef.current) {
                recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
                  'sitekey': RECAPTCHA_SITE_KEY,
                  'callback': (response) => {
                    console.log('reCAPTCHA completed:', response ? 'Success' : 'Failed');
                    if (mounted) {
                      setRecaptchaError(null); // Clear any previous errors
                    }
                  },
                  'expired-callback': () => {
                    console.log('reCAPTCHA expired');
                    if (mounted) {
                      setRecaptchaError('reCAPTCHA has expired. Please complete it again.');
                    }
                  },
                  'error-callback': () => {
                    console.log('reCAPTCHA error occurred');
                    if (mounted) {
                      setRecaptchaError('reCAPTCHA encountered an error. Please refresh the page and try again.');
                    }
                  }
                });
                
                if (mounted) {
                  setRecaptchaLoaded(true);
                  setRecaptchaError(null);
                  console.log('reCAPTCHA rendered successfully with widget ID:', recaptchaWidgetId.current);
                }
                return; // Success - exit the retry loop
              }
            } catch (error) {
              console.error('Error rendering reCAPTCHA:', error);
              
              // If it's a "reCAPTCHA has already been rendered" error, try to clean up first
              if (error.message && error.message.includes('already been rendered')) {
                cleanupRecaptcha();
              }
              
              if (attempts < maxAttempts && Date.now() - startTime < timeoutMs && mounted) {
                attempts++;
                timeoutId = setTimeout(tryRender, 500);
                return;
              } else if (mounted) {
                setRecaptchaError('Failed to initialize reCAPTCHA. Please refresh the page and try again.');
                setRecaptchaLoaded(false);
                return;
              }
            }
          }
          
          // Retry if not ready yet
          if (attempts < maxAttempts && Date.now() - startTime < timeoutMs && mounted) {
            attempts++;
            timeoutId = setTimeout(tryRender, 500);
          } else if (mounted) {
            setRecaptchaError('reCAPTCHA failed to load. Please check your internet connection and try again.');
            setRecaptchaLoaded(false);
          }
        };

        tryRender();
      } catch (error) {
        console.error("Error handling reCAPTCHA loading:", error);
        if (mounted) {
          setRecaptchaError('Failed to load reCAPTCHA. Please refresh the page and try again.');
          setRecaptchaLoaded(false);
        }
      }
    };

    initializeRecaptcha();

    // Cleanup function
    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      cleanupRecaptcha();
    };
  }, []); // Empty dependency array means this runs once on mount

  // Function to retry reCAPTCHA initialization
  const retryRecaptcha = () => {
    setRecaptchaError(null);
    setRecaptchaLoaded(false);
    
    // More thorough cleanup approach
    const performThoroughCleanup = () => {
      // Reset widget if it exists
      if (window.grecaptcha && recaptchaWidgetId.current !== null) {
        try {
          window.grecaptcha.reset(recaptchaWidgetId.current);
          console.log('Reset existing widget:', recaptchaWidgetId.current);
        } catch (error) {
          console.log('Error resetting widget during cleanup:', error);
        }
      }
      
      // Clear the container completely
      if (recaptchaRef.current) {
        // Remove all child nodes
        while (recaptchaRef.current.firstChild) {
          recaptchaRef.current.removeChild(recaptchaRef.current.firstChild);
        }
        // Also clear innerHTML as backup
        recaptchaRef.current.innerHTML = '';
        console.log('Container cleared');
      }
      
      // Reset widget ID
      recaptchaWidgetId.current = null;
    };
    
    // Perform cleanup
    performThoroughCleanup();
    
    // Add a small delay to ensure cleanup is complete
    setTimeout(() => {
      // Re-initialize
      const initializeRecaptcha = async () => {
        try {
          await loadReCaptchaScript();
          
          const maxAttempts = 10;
          const timeoutMs = 30000;
          let attempts = 0;
          const startTime = Date.now();

          const tryRender = () => {
            if (Date.now() - startTime > timeoutMs) {
              setRecaptchaError('reCAPTCHA failed to load within 30 seconds. Please refresh the page and try again.');
              return;
            }

            if (window.grecaptcha && window.grecaptcha.render && recaptchaRef.current) {
              try {
                // Final check - ensure container is completely empty
                if (recaptchaRef.current.children.length > 0) {
                  console.log('Container not empty, clearing again...');
                  while (recaptchaRef.current.firstChild) {
                    recaptchaRef.current.removeChild(recaptchaRef.current.firstChild);
                  }
                  recaptchaRef.current.innerHTML = '';
                }

                // Verify that grecaptcha is ready and no widget exists in this container
                if (window.grecaptcha.getResponse !== undefined && recaptchaRef.current && recaptchaWidgetId.current === null) {
                  console.log('Attempting to render reCAPTCHA...');
                  recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
                    'sitekey': RECAPTCHA_SITE_KEY,
                    'callback': (response) => {
                      console.log('reCAPTCHA completed:', response ? 'Success' : 'Failed');
                      setRecaptchaError(null);
                    },
                    'expired-callback': () => {
                      setRecaptchaError('reCAPTCHA has expired. Please complete it again.');
                    },
                    'error-callback': () => {
                      setRecaptchaError('reCAPTCHA encountered an error. Please try again.');
                    }
                  });
                  
                  console.log('reCAPTCHA rendered successfully with new widget ID:', recaptchaWidgetId.current);
                  setRecaptchaLoaded(true);
                  setRecaptchaError(null);
                  return; // Success - exit the retry loop
                }
              } catch (error) {
                console.error('Error rendering reCAPTCHA during retry:', error);
                
                // If it's still the "already rendered" error, do more aggressive cleanup
                if (error.message && error.message.includes('already been rendered')) {
                  console.log('Still getting "already rendered" error, performing aggressive cleanup...');
                  performThoroughCleanup();
                  
                  // Try to find and reset any existing widgets
                  if (window.grecaptcha && window.grecaptcha.reset) {
                    try {
                      // Try to reset with widget ID 0 (often the first widget)
                      window.grecaptcha.reset(0);
                    } catch (resetError) {
                      console.log('Error with aggressive reset:', resetError);
                    }
                  }
                }
                
                if (attempts < maxAttempts && Date.now() - startTime < timeoutMs) {
                  attempts++;
                  setTimeout(tryRender, 1000); // Longer delay for retry
                  return;
                } else {
                  setRecaptchaError('Failed to initialize reCAPTCHA. Please refresh the page.');
                  return;
                }
              }
            }
            
            // Retry if not ready yet
            if (attempts < maxAttempts && Date.now() - startTime < timeoutMs) {
              attempts++;
              setTimeout(tryRender, 500);
            } else {
              setRecaptchaError('reCAPTCHA failed to load. Please check your connection.');
            }
          };

          tryRender();
        } catch (error) {
          setRecaptchaError('Failed to load reCAPTCHA. Please refresh the page.');
        }
      };

      initializeRecaptcha();
    }, 100); // 100ms delay to ensure cleanup is complete
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    // Clear validation error for this field when user starts typing
    if (validationErrors[e.target.name]) {
      setValidationErrors({
        ...validationErrors,
        [e.target.name]: ''
      })
    }
  }

  // Email validation using regex (matching your JavaScript example)
  const validateEmail = (email) => {
    const emailRegex = /^\S+@\S+\.\S+$/
    return emailRegex.test(email)
  }

  // Form validation function
  const validateForm = () => {
    const errors = {}
    
    // Validate required fields
    if (!formData.name.trim()) {
      errors.name = 'Name is required'
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required'
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate form inputs
    if (!validateForm()) {
      alert('Please fill in all required fields with valid inputs.')
      return
    }

    setIsSubmitting(true)
    
    try {
      // Check if reCAPTCHA is loaded and available
      if (recaptchaError) {
        alert('reCAPTCHA is not available. Please refresh the page and try again.')
        setIsSubmitting(false)
        return
      }

      if (!recaptchaLoaded || !window.grecaptcha) {
        alert('reCAPTCHA is still loading. Please wait a moment and try again.')
        setIsSubmitting(false)
        return
      }

      // Check reCAPTCHA v2 response
      let recaptchaResponse = ''
      try {
        recaptchaResponse = window.grecaptcha.getResponse(recaptchaWidgetId.current)
      } catch (error) {
        console.error('Error getting reCAPTCHA response:', error)
        alert('reCAPTCHA error. Please refresh the page and try again.')
        setIsSubmitting(false)
        return
      }

      // Log the recaptcha response for debugging
      console.log('reCAPTCHA response:', recaptchaResponse)
      
      if (!recaptchaResponse) {
        alert('Please complete the reCAPTCHA verification.')
        setIsSubmitting(false)
        return
      }

      // Construct the form data (matching your JavaScript example structure)
      const submissionData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        'g-recaptcha-response': recaptchaResponse, // This now contains the actual reCAPTCHA response
        // Include optional fields if they have values
        ...(formData.company.trim() && { company: formData.company.trim() }),
        ...(formData.phone.trim() && { phone: formData.phone.trim() })
      }

      // Log the form data to console (matching your JavaScript example)
      console.log('Submitting form data:', JSON.stringify(submissionData, null, 2))

      // Make API request to backend
      const response = await fetch('https://mtr47cx46j.execute-api.us-west-2.amazonaws.com/default/DCWS-ContactForm-v1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: '',
          'g-recaptcha-response': ''
        })
        setValidationErrors({})
        // Reset reCAPTCHA v2
        if (window.grecaptcha && window.grecaptcha.reset && recaptchaWidgetId.current !== null) {
          try {
            window.grecaptcha.reset(recaptchaWidgetId.current);
          } catch (error) {
            console.log('Error resetting reCAPTCHA after successful submission:', error);
          }
        }
      } else {
        const errorText = await response.text()
        throw new Error(`Form submission failed: ${response.status} ${errorText}`)
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setSubmitStatus('error')
      // Enhanced error message matching your JavaScript example
      const submissionDataForLog = {
        ...formData,
        'g-recaptcha-response': recaptchaResponse || 'FAILED_TO_GENERATE'
      }
      const errorMessage = `Form submission failed. Please try again later. Error: ${error.message}\nForm Data: ${JSON.stringify(submissionDataForLog, null, 2)}`
      console.error(errorMessage)
      alert('Form submission failed. Please try again later.')
    } finally {
      setIsSubmitting(false)
      // Reset status after 3 seconds
      setTimeout(() => setSubmitStatus(null), 3000)
    }
  }

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'contactus@praetorian.works',
      subDetails: 'We typically respond within 24 hours',
      action: 'mailto:contactus@praetorian.works'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: '+1 (408) 634-9679',
      subDetails: 'Monday - Friday, 9:00 AM - 6:00 PM PST',
      action: 'tel:+14086349679'
    },
    {
      icon: UserRound,
      title: 'Meet with Us',
      details: 'San Francisco, CA',
      subDetails: 'Silicon Valley Technology Hub',
      action: () => window.open('https://outlook.office.com/bookwithme/user/79162131c06a43749cbc74a3bec81aeb@disruption.consulting/meetingtype/3peO1Jnpeke4DQtqwEvvuQ2?bookingcode=b4b20334-a18b-444b-ac94-88c3c7128719&anonymous&ismsaljsauthenabled&ep=mlink', '_blank')
    }
  ]

  const offices = [
    {
      city: 'Silicon Valley',
      address: 'San Francisco - San Jose',
      phone: '+1 (408) 634-9679',
      hours: 'Mon-Fri: 7:00 AM - 6:00 PM PST'
    },
    {
      city: 'Southern California',
      address: 'Los Angeles - San Diego',
      phone: '+1 (408) 634-9679',
      hours: 'Mon-Fri: 7:00 AM - 6:00 PM PST'
    },
    {
      city: 'Great Lakes',
      address: 'Chicago - Detroit',
      phone: '+1 (408) 634-9679',
      hours: 'Mon-Fri: 9:00 AM - 6:00 PM CST'
    },
    {
      city: 'New York',
      address: 'Manhattan - New Jersey',
      phone: '+1 (408) 634-9679',
      hours: 'Mon-Fri: 9:00 AM - 6:00 PM EST'
    }
  ]

  const [isModalOpen, setIsModalOpen] = useState(false)

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => setIsModalOpen(false)

  return (
    <>
      <SEO 
        title="Contact Us"
        description="Get in touch with Praetorian Works for elite technology services. Expert white glove support for High Net Worth and Ultra High Net Worth individuals. Schedule a private consultation today."
        keywords="contact praetorian works, HNW technology services, UHNW tech support, elite IT concierge, luxury technology consultation"
        canonical="/contact"
      />
      
      <div className="pt-20">
      {/* Header Section */}
      <section className="section-padding bg-black/30">
        <div className="container-max text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            Contact <span className="text-primary-500">Us</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-neutral-300 max-w-4xl mx-auto leading-relaxed"
          >
            Ready to experience elite technology services designed exclusively for you? 
            Let's discuss how Praetorian Works can elevate your digital life.
          </motion.p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="section-padding bg-black/30">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center space-y-4 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center mx-auto shadow-lg transition-all duration-300">
                  <info.icon className="w-8 h-8 text-secondary-900 transition-colors duration-300" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-white">{info.title}</h3>
                  {info.action ? (
                    <a 
                      href={info.action}
                      className="block text-lg font-medium text-primary-400 hover:text-primary-500 transition-colors duration-200"
                    >
                      {info.details}
                    </a>
                  ) : (
                    <p className="text-lg font-medium text-primary-500">{info.details}</p>
                  )}
                  <p className="text-neutral-400 text-sm">{info.subDetails}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact Form and Info Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-secondary-800 border border-neutral-800 rounded-2xl p-8"
            >
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Send us a Message
                </h2>
                <p className="text-neutral-400">
                  Fill out the form below and we'll get back to you quickly.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-neutral-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      autoComplete="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-secondary-700 text-white border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                        validationErrors.name ? 'border-red-500' : 'border-neutral-700'
                      }`}
                      placeholder="John Doe"
                    />
                    {validationErrors.name && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-secondary-700 text-white border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                        validationErrors.email ? 'border-red-500' : 'border-neutral-700'
                      }`}
                      placeholder="john@company.com"
                    />
                    {validationErrors.email && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-neutral-300 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      autoComplete="organization"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-secondary-700 text-white border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                      placeholder="Your Company"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-neutral-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-secondary-700 text-white border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-neutral-300 mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    autoComplete="off"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-secondary-700 text-white border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                      validationErrors.subject ? 'border-red-500' : 'border-neutral-700'
                    }`}
                  >
                    <option value="">Select a subject</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Managed Services">Managed Services</option>
                    <option value="Consulting Services">Consulting Services</option>
                    <option value="AI Solutions">AI Solutions</option>
                    <option value="Digital Transformation">Digital Transformation</option>
                    <option value="Partnership Opportunities">Partnership Opportunities</option>
                    <option value="Technical Support">Technical Support</option>
                  </select>
                  {validationErrors.subject && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.subject}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-neutral-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="6"
                    autoComplete="off"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-secondary-700 text-white border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 resize-none ${
                      validationErrors.message ? 'border-red-500' : 'border-neutral-700'
                    }`}
                    placeholder="Tell us about your project or how we can help..."
                  ></textarea>
                  {validationErrors.message && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.message}</p>
                  )}
                </div>

                {/* reCAPTCHA v2 Checkbox */}
                <div className="flex flex-col items-center space-y-2">
                  <div ref={recaptchaRef} className="transform scale-90 sm:scale-100"></div>
                  
                  {!recaptchaLoaded && !recaptchaError && (
                    <div className="text-gray-500 text-sm">Loading reCAPTCHA...</div>
                  )}
                  
                  {recaptchaError && (
                    <div className="text-center space-y-2">
                      <p className="text-red-500 text-sm">{recaptchaError}</p>
                      <button
                        type="button"
                        onClick={retryRecaptcha}
                        className="px-4 py-2 text-sm bg-primary-400 hover:bg-primary-500 text-white rounded-lg transition-colors duration-200"
                      >
                        Retry reCAPTCHA
                      </button>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full flex items-center justify-center px-6 py-4 rounded-lg font-medium transition-all duration-200 ${
                    isSubmitting
                      ? 'bg-neutral-600 text-neutral-400 cursor-not-allowed'
                      : 'btn-primary'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 w-4 h-4" />
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <p className="text-green-800 text-sm">
                      Thank you! Your message has been sent successfully. We'll get back to you quickly.
                    </p>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <p className="text-red-800 text-sm">
                      Sorry, there was an error sending your message. Please try again later.
                    </p>
                  </motion.div>
                )}
              </form>
            </motion.div>

            {/* Office Information */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-2xl font-bold text-primary-500 mb-6">
                  Our Focus Markets
                </h2>
                <div className="space-y-6">
                  {offices.map((office, index) => (
                    <div key={office.city} className="bg-secondary-600 rounded-xl p-6 border border-secondary-200 hover:border-primary-400 transition-colors duration-200">
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-secondary-600" />
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold text-primary-400">{office.city}</h3>
                          <p className="text-neutral-50">{office.address}</p>
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2 text-sm text-neutral-50">
                              <Phone className="w-4 h-4" />
                              <span>{office.phone}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-neutral-50">
                              <Clock className="w-4 h-4" />
                              <span>{office.hours}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Info */}
                    <div className="bg-secondary-600 border-2 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-primary-400 mb-4 flex items-center">
                        <Globe className="w-5 h-5 mr-2 text-primary-500" />
                        Global Reach
                      </h3>
                      <p className="text-neutral-50 leading-relaxed">
                        While we're headquartered in California, our team works with clients 
                        nationally and internationally, in the global banking and technology centers worldwide. We have consultants and partners across North America, Europe, 
                        and Asia-Pacific to support your global technology initiatives.
                      </p>
                    </div>

                    <div className="bg-gradient-to-r border-2 from-secondary-600 to-secondary-700 rounded-xl p-6 text-white">
                      <h3 className="text-lg font-semibold mb-4">
                        Ready to Get Started?
                      </h3>
                      <p className="text-neutral-50 leading-relaxed mb-4">
                        Schedule a free consultation with our technology experts to discuss 
                        your specific needs and how we can help accelerate your digital transformation.
                      </p>
                      <button 
                        onClick={() => window.open('https://outlook.office.com/bookwithme/user/79162131c06a43749cbc74a3bec81aeb@disruption.consulting/meetingtype/3peO1Jnpeke4DQtqwEvvuQ2?bookingcode=b4b20334-a18b-444b-ac94-88c3c7128719&anonymous&ismsaljsauthenabled&ep=mlink', '_blank')}
                        className="inline-flex items-center px-4 py-2 bg-white text-primary-600 hover:bg-primary-50 font-medium rounded-lg transition-colors duration-200"
                      >
                        Schedule Consultation
                      </button>
                    </div>
                    </motion.div>
                    </div>
                  </div>
                  </section>

                  {/* Booking Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900">Schedule Consultation</h3>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-secondary-100 rounded-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <iframe
              src="https://outlook.office.com/bookwithme/user/79162131c06a43749cbc74a3bec81aeb@disruption.consulting/meetingtype/5h0fOJpOPEKj4WH4aWsYsQ2?anonymous&ep=mlink"
              className="w-full h-[600px]"
              frameBorder="0"
              title="Schedule Consultation"
            />
          </div>
        </div>
      )}
      </div>
    </>
  )
}

export default Contact